import React from 'react'
import { Icon } from '@chakra-ui/icons'
import { Text } from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import {
    FaCommentDots,
    FaEnvelope,
    FaMessage,
    FaNoteSticky,
    FaAngleDown,
} from 'react-icons/fa6'
import {
    Popover,
    PopoverTrigger,
    PopoverBody,
    PopoverContent,
    PopoverArrow,
    PopoverFooter,
} from '@chakra-ui/react'

import ClientPhoneSelect from './client-phone'
import SharedStack from '~components/shared/wrappers/shared-stack'
import {
    ChatFieldsFragment,
    ClientPhoneFieldsFragment,
    MessageMethod,
} from '~graphql/generated/graphql'
import IconWithTooltip from '~components/shared/icons/icon-with-tooltip'
import { getPhoneCountryFromCode } from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'
import ChatChannelSelect from './channel-select'
import { SetStateFn } from '~config/constants'

interface Props {
    chat: ChatFieldsFragment
    messageMethods: MessageMethod[]
    availablePhones: ClientPhoneFieldsFragment[]
    clientPhone: ClientPhoneFieldsFragment | undefined
    setClientPhone: SetStateFn<ClientPhoneFieldsFragment | undefined>
    baseClientPhone: string | null | undefined
    setMessageMethods: SetStateFn<MessageMethod[]>
    resetMessageMethods: () => void
    smsDisabledMessage: string | undefined | null
    emailDisabledMessage: string | undefined | null
    isInternal?: boolean
    setIsInternal?: (val: boolean) => void
}

export default function MethodsSelect({
    chat,
    messageMethods,
    availablePhones,
    clientPhone,
    setClientPhone,
    baseClientPhone,
    setMessageMethods,
    resetMessageMethods,
    smsDisabledMessage,
    emailDisabledMessage,
    isInternal = false,
    setIsInternal,
}: Props) {
    return (
        <Popover placement="top-start" returnFocusOnClose={false}>
            <PopoverTrigger>
                <Button
                    size="xs"
                    variant="ghost"
                    rounded="full"
                    rightIcon={<Icon as={FaAngleDown} />}
                >
                    <SharedStack row fit gap={2} divider={<span>&</span>}>
                        {getMethodsButtonText(
                            chat,
                            messageMethods,
                            isInternal,
                            clientPhone,
                        )}
                    </SharedStack>
                </Button>
            </PopoverTrigger>
            <PopoverContent rounded="2xl">
                <PopoverArrow />
                <PopoverBody px={2} py={2}>
                    <ChatChannelSelect
                        messageMethods={messageMethods}
                        setMessageMethods={setMessageMethods}
                        smsDisabledMessage={smsDisabledMessage}
                        emailDisabledMessage={emailDisabledMessage}
                        isInternal={isInternal}
                        setIsInternal={setIsInternal}
                    />
                    <ClientPhoneSelect
                        chat={chat}
                        messageMethods={messageMethods}
                        availablePhones={availablePhones}
                        clientPhone={clientPhone}
                        setClientPhone={setClientPhone}
                        baseClientPhone={baseClientPhone}
                    />
                </PopoverBody>
                <PopoverFooter>
                    <Button
                        size="xs"
                        variant="ghost"
                        rounded="full"
                        onClick={resetMessageMethods}
                    >
                        Reset
                    </Button>
                </PopoverFooter>
            </PopoverContent>
        </Popover>
    )
}

function getMethodsButtonText(
    chat: ChatFieldsFragment,
    messageMethods: MessageMethod[],
    isInternal: boolean,
    clientPhone?: ClientPhoneFieldsFragment,
): React.ReactNode[] {
    if (isInternal) {
        return [
            <SharedStack row fit spacing={1}>
                <Icon as={FaNoteSticky} />
                <Text>Internal Note</Text>
            </SharedStack>,
        ]
    }

    if (messageMethods.length === 0) {
        return [
            <SharedStack row fit spacing={1}>
                <Icon as={FaMessage} />
                <Text>Chckvet Chat</Text>
            </SharedStack>,
        ]
    }

    const methods: React.ReactNode[] = []

    if (messageMethods.includes(MessageMethod.Email)) {
        methods.push(
            <SharedStack row fit spacing={1}>
                <Icon as={FaEnvelope} />
                <Text>Email</Text>
            </SharedStack>,
        )
    }

    if (messageMethods.includes(MessageMethod.Sms)) {
        methods.push(
            <SharedStack row fit spacing={1}>
                <IconWithTooltip
                    icon={FaCommentDots}
                    label={
                        clientPhone?.phone
                            ? `Sending to ${formatPhoneReadable(
                                  clientPhone.phone,
                                  clientPhone?.country_code
                                      ? getPhoneCountryFromCode(
                                            clientPhone?.country_code ?? '+1',
                                        )
                                      : chat.clinic?.address?.country,
                              )} ${clientPhone?.name ? `(${clientPhone?.name})` : ''}`
                            : ''
                    }
                />
                <Text>SMS</Text>
            </SharedStack>,
        )
    }

    return methods
}
